import * as React from 'react';
import { debounce } from 'lodash';

import { Checkbox, Input, Button } from 'src/components';
import API from 'src/services/api';
import { Canceler } from 'axios';

interface ICreateShortlinkProps {
  linkId: number;
  onGenerated?: (url: string) => void;
}
interface ICreateShortlinkState {
  isFormOpen: boolean;
  automatic: boolean;
  url: string;
  previewURL: string;
  loadingPreview: boolean;
  generating: boolean;
  error: string;
}

export default class CreateShortlink extends React.Component<
  ICreateShortlinkProps,
  ICreateShortlinkState
> {
  reqCanceler: Canceler | null = null;

  state: ICreateShortlinkState = {
    isFormOpen: false,
    automatic: true,
    url: '',
    previewURL: '—',
    loadingPreview: false,
    generating: false,
    error: '',
  };

  componentWillUnmount() {
    if (this.reqCanceler) {
      this.reqCanceler();
    }
  }

  toggleForm = () => {
    this.setState((state) => ({ isFormOpen: !state.isFormOpen }));
  };

  toggleAutomatic = (state: boolean) => {
    this.setState({ automatic: state });
  };

  generate = async () => {
    this.setState({ generating: true });

    const { getData, cancel } = API.SHORTLINK.create(
      this.props.linkId,
      this.state.automatic ? '' : this.state.url
    );
    this.reqCanceler = cancel;
    const shortlink = await getData();
    this.reqCanceler = null;

    this.setState({ generating: false });
    if (!shortlink.preview) {
      this.setState({ error: shortlink.message });
      return;
    }
    if (this.props.onGenerated) {
      this.props.onGenerated(shortlink.preview);
    }
  };

  updatePreview = async () => {
    if (this.reqCanceler) {
      this.reqCanceler();
    }
    const { url } = this.state;
    if (url.length < 2) {
      this.setState({ previewURL: '—' });
      return;
    }
    this.setState({ loadingPreview: true, error: '' });
    const { getData, cancel } = API.SHORTLINK.validate(this.props.linkId, url);
    this.reqCanceler = cancel;
    try {
      const shortlink = await getData();
      if (!shortlink.preview) {
        this.setState({ loadingPreview: false, error: shortlink.message });
      } else {
        this.setState({
          loadingPreview: false,
          previewURL: shortlink.preview,
        });
      }
    } catch (error) {
      this.setState({
        loadingPreview: false,
        error: 'An error occured',
      });
    }
    this.reqCanceler = null;
  };
  debouncedUpdatePreview = debounce(this.updatePreview, 300);

  handleURLChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState(
      { url: event.currentTarget.value },
      this.debouncedUpdatePreview
    );
  };

  render() {
    const { linkId } = this.props;
    const {
      isFormOpen,
      automatic,
      url,
      previewURL,
      loadingPreview,
      generating,
      error,
    } = this.state;
    return (
      <>
        {isFormOpen ? (
          <div className="relative bg-grey-light my-4 px-4 py-6">
            <Button
              className="absolute pin-t pin-r mr-4 mt-3"
              background="grey-light"
              size="small"
              onClick={this.toggleForm}
            >
              x
            </Button>

            <Checkbox
              type="radio"
              name={`option-${linkId}`}
              id={`automatic-${linkId}`}
              checked={automatic}
              onChange={this.toggleAutomatic.bind(null, true)}
              className="inline-block mx-4 mb-4"
            >
              Generate automatically
            </Checkbox>
            <Checkbox
              type="radio"
              name={`option-${linkId}`}
              id={`semantic-${linkId}`}
              checked={!automatic}
              onChange={this.toggleAutomatic.bind(null, false)}
              className="inline-block mx-4 mb-4"
            >
              Create Semantic URL:
            </Checkbox>

            <div className="mb-4">
              <Input
                className="w-full mb-2"
                disabled={automatic}
                value={url}
                onChange={this.handleURLChange}
              />
              <p className={`text-sm ${automatic ? 'opacity-50' : ''}`}>
                {error ? (
                  <span className="text-red">{error}</span>
                ) : (
                  <>
                    Preview:{' '}
                    <span
                      className={`font-mono inline-block px-1 align-middle ${
                        loadingPreview ? 'loader' : ''
                      }`}
                    >
                      {previewURL}
                    </span>
                  </>
                )}
              </p>
            </div>

            <Button
              size="small"
              background="grey-light"
              onClick={this.generate}
              loading={generating}
              loadingLabel="Generating shortlink"
              disabled={generating || (!automatic && url.length < 2)}
            >
              Generate shortlink
            </Button>
          </div>
        ) : (
          <Button size="small" background="grey" onClick={this.toggleForm}>
            Create shortlink
          </Button>
        )}
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/CreateShortlink.tsx